/**
 * Page and external link shortcodes for marketing site content.
 *
 * Internal: [page:download], [page:download text="Get the app" target="_blank"]
 * External: [link:https://example.com], [link url="…" text="…" target="_self"]
 */

import { isOnboardingCmsSlug } from '@/lib/cms/onboardingSlug'

export interface ShortcodePageRef {
  slug: string
  title: string
}

const ATTR_RE = /([a-zA-Z_][\w-]*)\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s\]]+))/g

/** Allowed HTML target values (browsing context names + keywords). */
const TARGET_RE = /^(_blank|_self|_parent|_top|[a-zA-Z][\w-]*)$/

/** [page:slug] or [page:slug text="…" target="_blank"] */
const PAGE_COLON_RE =
  /\[page:([a-zA-Z0-9_/-]+)(?:\s+([^\]]+))?\]/gi

/** [page name="About"] or [page name="About" text="Learn more" target="_blank"] */
const PAGE_ATTR_RE = /\[page\s+([^\]]+)\]/gi

/** [link:https://…] or [link:https://… text="…" target="_self"] */
const LINK_COLON_RE =
  /\[link:(https?:\/\/[^\s\]]+)(?:\s+([^\]]+))?\]/gi

/** [link url="…" text="…" target="…"] */
const LINK_ATTR_RE = /\[link\s+([^\]]+)\]/gi

function parseAttrs(raw: string): Record<string, string> {
  const attrs: Record<string, string> = {}
  const re = new RegExp(ATTR_RE.source, 'g')
  let match: RegExpExecArray | null
  while ((match = re.exec(raw)) !== null) {
    attrs[match[1].toLowerCase()] = match[2] ?? match[3] ?? match[4] ?? ''
  }
  return attrs
}

/** Legacy CMS slugs → current published page slugs */
const SLUG_ALIASES: Record<string, string> = {
  privacy: 'privacy-policy',
  terms: 'terms-of-use',
  'terms-of-service': 'terms-of-use',
}

/** CMS slug → dedicated SPA path (clean URLs without `/pages/` prefix). */
export const CMS_PAGE_ROUTES: Record<string, string> = {
  'terms-of-use': '/terms',
  'privacy-policy': '/privacy-policy',
  support: '/support',
  contact: '/support/contact',
}

/** Legacy `/pages/:slug` paths that should redirect to {@link CMS_PAGE_ROUTES}. */
export const LEGACY_CMS_PAGE_REDIRECTS: Record<string, string> = {
  'terms-of-use': CMS_PAGE_ROUTES['terms-of-use'],
  'privacy-policy': CMS_PAGE_ROUTES['privacy-policy'],
  support: CMS_PAGE_ROUTES.support,
}

export function canonicalizePageSlug(slug?: string | null): string {
  const cleaned = String(slug ?? '').replace(/^\/+|\/+$/g, '')
  if (!cleaned || cleaned === 'home') return ''
  return SLUG_ALIASES[cleaned.toLowerCase()] || cleaned
}

/** In-app href for a CMS page slug. */
export function pageHref(slug?: string | null): string {
  const cleaned = canonicalizePageSlug(slug)
  if (!cleaned || isOnboardingCmsSlug(cleaned)) return '/'
  const route = CMS_PAGE_ROUTES[cleaned.toLowerCase()]
  if (route) return route
  return `/pages/${cleaned}`
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function prettifySlug(slug?: string | null): string {
  const cleaned = String(slug ?? '').replace(/^\/+|\/+$/g, '')
  if (!cleaned || cleaned === 'home') return 'Home'
  return cleaned
    .split(/[-_/]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

/**
 * Normalize a shortcode `target` attr. Returns undefined when empty/invalid.
 * Accepts `blank` as an alias for `_blank`.
 */
export function normalizeLinkTarget(raw: string | undefined): string | undefined {
  const value = raw?.trim()
  if (!value) return undefined
  const normalized = value === 'blank' ? '_blank' : value
  return TARGET_RE.test(normalized) ? normalized : undefined
}

/**
 * Resolve a page ref by slug or title (case-insensitive).
 */
export function resolvePageRef(
  ref: string,
  pages: ShortcodePageRef[] = [],
): { href: string; label: string } {
  const rawKey = ref.trim().toLowerCase().replace(/^\/+|\/+$/g, '')
  const key = canonicalizePageSlug(rawKey)
  const homeKeys = new Set(['', 'home', 'index'])

  const bySlug = pages.find((p) => {
    const slug = canonicalizePageSlug(p.slug || '').toLowerCase()
    if (homeKeys.has(key)) return !slug
    return slug === key
  })
  const byTitle = pages.find((p) => p.title.trim().toLowerCase() === rawKey)

  const page = bySlug || byTitle
  if (page) {
    return { href: pageHref(page.slug), label: page.title || prettifySlug(ref) }
  }

  return { href: pageHref(ref), label: prettifySlug(ref) }
}

function anchorHtml(
  href: string,
  label: string,
  options: { external?: boolean; target?: string } = {},
): string {
  const safeHref = escapeHtml(href)
  const safeLabel = escapeHtml(label)
  const explicit = normalizeLinkTarget(options.target)
  // Explicit target wins; otherwise external links default to a new tab.
  const target =
    explicit ?? (options.external ? '_blank' : undefined)

  const attrs = [`href="${safeHref}"`]
  if (target) {
    attrs.push(`target="${escapeHtml(target)}"`)
    if (target === '_blank') {
      attrs.push('rel="noopener noreferrer"')
    }
  }
  return `<a ${attrs.join(' ')}>${safeLabel}</a>`
}

function isExternalUrl(url: string): boolean {
  return /^https?:\/\//i.test(url.trim())
}

/**
 * If `text` is solely a page/link shortcode, return the href only
 * (for use in JSON props like `cta.href`). Otherwise null.
 */
export function resolveHrefOnlyShortcode(
  text: string,
  pages: ShortcodePageRef[] = [],
): string | null {
  const trimmed = text.trim()
  const pageColon = trimmed.match(/^\[page:([a-zA-Z0-9_/-]+)\]$/i)
  if (pageColon) return resolvePageRef(pageColon[1], pages).href

  const pageAttrs = trimmed.match(/^\[page\s+([^\]]+)\]$/i)
  if (pageAttrs) {
    const attrs = parseAttrs(pageAttrs[1])
    const ref = attrs.name || attrs.slug || attrs.page || ''
    if (ref) return resolvePageRef(ref, pages).href
  }

  const linkColon = trimmed.match(/^\[link:(https?:\/\/[^\s\]]+)\]$/i)
  if (linkColon) return linkColon[1].trim()

  const linkAttrs = trimmed.match(/^\[link\s+([^\]]+)\]$/i)
  if (linkAttrs) {
    const attrs = parseAttrs(linkAttrs[1])
    const href = (attrs.url || attrs.href || '').trim()
    return href || null
  }

  return null
}

/**
 * Replace [page:…] and [link:…] shortcodes with HTML anchors.
 * Run before markdown parsing so raw HTML is preserved by marked.
 */
export function replaceLinkShortcodes(
  text: string,
  pages: ShortcodePageRef[] = [],
): string {
  let out = text

  out = out.replace(PAGE_COLON_RE, (_m, ref: string, attrRaw?: string) => {
    const attrs = attrRaw ? parseAttrs(attrRaw) : {}
    const resolved = resolvePageRef(ref, pages)
    const label = attrs.text?.trim() || resolved.label
    return anchorHtml(resolved.href, label, { target: attrs.target })
  })

  out = out.replace(PAGE_ATTR_RE, (_m, attrRaw: string) => {
    const attrs = parseAttrs(attrRaw)
    const ref = attrs.name || attrs.slug || attrs.page || ''
    if (!ref) return _m
    const resolved = resolvePageRef(ref, pages)
    const label = attrs.text?.trim() || resolved.label
    return anchorHtml(resolved.href, label, { target: attrs.target })
  })

  out = out.replace(LINK_COLON_RE, (_m, url: string, attrRaw?: string) => {
    const attrs = attrRaw ? parseAttrs(attrRaw) : {}
    const href = url.trim()
    const label = attrs.text?.trim() || href
    return anchorHtml(href, label, { external: true, target: attrs.target })
  })

  out = out.replace(LINK_ATTR_RE, (_m, attrRaw: string) => {
    const attrs = parseAttrs(attrRaw)
    const href = (attrs.url || attrs.href || '').trim()
    if (!href) return _m
    const label = attrs.text?.trim() || href
    return anchorHtml(href, label, {
      external: isExternalUrl(href),
      target: attrs.target,
    })
  })

  return out
}
